/**
 * 書式の注意（docs/31 §5「警告」・§11-5、#217 ■4）。色だけで伝えず文で出す。エディタとテスト印刷の両方で同じ関数。
 *   ・重なり：部品どうし・明細表（表の下の枠も）と部品・明細の列どうし。ロゴ・社印のように画像が未登録でも、置いた枠の場所で見る。
 *     「重なりを許可」（overlap）の部品は数えない（社印は既定で許可＝会社名に重ねて押す慣例）。線と枠（飾り）は数えない
 *   ・通し番号の帯（#223 送付状の FAX の型、docs/89 §15 の7）：下の中央・幅 140mm・紙の下端から 5〜11mm に「n／N　書類名 番号」を重ねる。
 *     部品・明細表がこの帯に重なると重なりの注意（相手は「通し番号（FAX）」・target は部品、other は "through"）。重なりを許可した部品も数える（帯は必ず刷られる）
 *   ・紙の端：紙の端 5mm より外
 *   ・はみ出し：見本のデータ（か本物の中身）で文字が欄・枠に入りきらない（規格が切れる・備考の本文が枠から出る・金額を小さくしすぎる）
 *   ・薄い色：白地でコントラスト 4.5 未満
 *   ・消せない部品が無い・印字が切られている／画像が未選択／二次元コードの中身が空／明細表と明細の下の部品が紙に入らない
 * 1件ごとに 種類・部品・相手・mm と、エディタが赤く塗る範囲（rect）を返す
 */
import { THROUGH_BAND_NAME, throughBandBox, type PaperData, type PaperLayout } from "@genbaryoku/contract";
import { contrast } from "./color.js";
import { sampleData } from "./data.js";
import type { Box } from "./engine.js";
import { dockHeight2, inkBox } from "./engine.js";
import { paperGeometry } from "./entry.js";
import { missingRequired, partOf } from "./presets.js";
import { tableHeight } from "./render.js";

/** 通し番号の帯の部品の名前（重なりの注意の other）。エディタはこの id で帯を朱にする */
export const THROUGH_BAND_ID = "through";

/** 重なりを許可しているか。決めていない部品は 社印（会社のロゴの社印）だけ許可（会社名に重ねて押す慣例） */
export const overlapAllowed = (e: PaperLayout["els"][number]): boolean => e.overlap ?? (e.type === "image" && e.asset === "company:seal");

export type WarnKind = "overlap" | "edge" | "overflow" | "color" | "required" | "image" | "qr" | "space";

/** 種類の言葉（注意の一覧の左の列） */
export const WARN_KIND_LABEL: Record<WarnKind, string> = {
  overlap: "重なり", edge: "紙の端", overflow: "はみ出し", color: "薄い色", required: "消せない部品", image: "画像", qr: "二次元コード", space: "入りきらない",
};

/**
 * 注意の1件。target＝枠で囲む部品（部品の id・明細表は "table"。消せない部品が無いときは null）。other＝重なりの相手。
 * name・otherName は部品の名前、mm は重なり・はみ出しの量、rect は赤い斜線を引く範囲（紙の上の mm）
 */
export type LayoutWarning = {
  kind: WarnKind;
  text: string;
  target: string | null;
  name: string;
  other?: string;
  otherName?: string;
  mm?: number;
  rect?: Box;
};

const r1 = (v: number): number => Math.round(v * 10) / 10;
const EDGE = 5;
const TOL = 0.3;

/** 警告と、その部品（エディタの「注意 n」のチップ → 一覧 → 部品を選んで表示を寄せる。#171・#217） */
export function layoutWarningItems(layout: PaperLayout, data?: PaperData, o: { construction?: boolean } = {}): LayoutWarning[] {
  const out: LayoutWarning[] = [];
  const pg = layout.page;
  const t = layout.table;
  const d = data ?? sampleData(layout.kind);
  const g = paperGeometry(layout, d);
  const nameOf = (id: string): string => (id === "table" ? "明細表" : (layout.els.find((e) => e.id === id)?.name || layout.els.find((e) => e.id === id)?.type || id));
  // 部品ごと
  for (const e of layout.els) {
    if (!e.on) continue;
    const b = g.els[e.id] ?? { x: e.x, y: e.y, w: e.w, h: e.h };
    const name = e.name || e.type;
    if (!(e.type === "image" && e.guide)) {
      const over = Math.max(EDGE - b.x, EDGE - b.y, b.x + b.w - (pg.w - EDGE), b.y + b.h - (pg.h - EDGE));
      if (over > 0.05) out.push({ kind: "edge", text: `「${name}」が紙の端 5mm より外に出ています（${r1(over)}mm）`, target: e.id, name, mm: r1(over) });
    }
    if ((e.type === "text" || e.type === "field") && e.color && contrast(e.color, "#FFFFFF") < 4.5) out.push({ kind: "color", text: `「${name}」の文字の色が薄すぎます（白地で読みにくい）`, target: e.id, name });
    if (e.type === "image" && !e.asset) out.push({ kind: "image", text: `「${name || "画像"}」に画像が選ばれていません（印刷には出ません）`, target: e.id, name });
    if (e.type === "qr" && !e.bind && !e.text.trim()) out.push({ kind: "qr", text: `「${name || "二次元コード"}」の中身が空です（印刷には出ません）`, target: e.id, name });
  }
  // 重なり：文字・項目・画像・二次元コードと明細表（線と枠は飾りなので数えない）
  type R = { id: string; name: string; b: Box; allow: boolean };
  const rs: R[] = [];
  for (const e of layout.els) {
    if (!e.on || e.type === "line" || e.type === "box" || (e.type === "image" && e.guide)) continue;
    const b = g.els[e.id];
    if (!b) continue;
    rs.push({ id: e.id, name: e.name || e.type, b, allow: overlapAllowed(e) });
  }
  rs.push({ id: "table", name: "明細表", b: g.table, allow: false });
  for (const bx of g.boxes) rs.push({ id: "table", name: "明細表", b: bx, allow: false });
  const seen = new Set<string>();
  for (let i = 0; i < rs.length; i++) {
    for (let j = i + 1; j < rs.length; j++) {
      const a = rs[i]!, c = rs[j]!;
      if (a.id === c.id || a.allow || c.allow) continue;
      const x0 = Math.max(a.b.x, c.b.x), x1 = Math.min(a.b.x + a.b.w, c.b.x + c.b.w);
      const y0 = Math.max(a.b.y, c.b.y), y1 = Math.min(a.b.y + a.b.h, c.b.y + c.b.h);
      if (x1 - x0 <= TOL || y1 - y0 <= TOL) continue;
      const key = [a.id, c.id].sort().join("|");
      if (seen.has(key)) continue;
      seen.add(key);
      const mm = r1(Math.min(x1 - x0, y1 - y0));
      out.push({ kind: "overlap", text: `「${a.name}」と「${c.name}」が重なっています（${mm}mm）`, target: a.id, name: a.name, other: c.id, otherName: c.name, mm, rect: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } });
    }
  }
  // 通し番号の帯（FAX の型の送付状で全ページの下の中央に重ねる）。字の部品は字の置かれる範囲で見る（左寄せの帳票名・右寄せのページ番号は数えない）
  const band = throughBandBox(pg);
  const bandItems: R[] = [];
  for (const e of layout.els) {
    if (!e.on || e.type === "line" || e.type === "box" || (e.type === "image" && e.guide)) continue;
    const b = g.els[e.id];
    if (b) bandItems.push({ id: e.id, name: e.name || e.type, b: inkBox(e, b, d), allow: false });
  }
  for (const r of rs) if (r.id === "table") bandItems.push(r);
  const bandSeen = new Set<string>();
  for (const a of bandItems) {
    if (bandSeen.has(a.id)) continue;
    const x0 = Math.max(a.b.x, band.x), x1 = Math.min(a.b.x + a.b.w, band.x + band.w);
    const y0 = Math.max(a.b.y, band.y), y1 = Math.min(a.b.y + a.b.h, band.y + band.h);
    if (x1 - x0 <= TOL || y1 - y0 <= TOL) continue;
    bandSeen.add(a.id);
    const mm = r1(Math.min(x1 - x0, y1 - y0));
    out.push({ kind: "overlap", text: `「${a.name}」と「${THROUGH_BAND_NAME}」が重なっています（${mm}mm）`, target: a.id, name: a.name, other: THROUGH_BAND_ID, otherName: THROUGH_BAND_NAME, mm, rect: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } });
  }
  // 明細の列どうし
  const cols = t.cols.filter((c) => c.on).sort((a, b) => a.x - b.x);
  for (let i = 1; i < cols.length; i++) {
    const a = cols[i - 1]!, b = cols[i]!;
    const ov = a.x + a.w - b.x;
    if (ov > 0.05) {
      const an = a.label || a.id, bn = b.label || b.id;
      out.push({ kind: "overlap", text: `明細の列「${an}」と「${bn}」が重なっています（${r1(ov)}mm）`, target: "table", name: `明細の列「${an}」`, other: "table", otherName: `明細の列「${bn}」`, mm: r1(ov), rect: { x: t.x + b.x, y: g.table.y, w: ov, h: g.table.h } });
    }
  }
  const lastCol = cols[cols.length - 1];
  if (lastCol && lastCol.x + lastCol.w > t.w + 0.05) {
    const ov = lastCol.x + lastCol.w - t.w;
    out.push({ kind: "overflow", text: `明細の列が表の幅からはみ出しています（${r1(ov)}mm）`, target: "table", name: "明細表", mm: r1(ov), rect: { x: t.x + t.w, y: g.table.y, w: ov, h: g.table.h } });
  }
  if (t.x < EDGE || t.x + t.w > pg.w - EDGE) out.push({ kind: "edge", text: "明細表が紙の端 5mm より外に出ています", target: "table", name: "明細表" });
  // 1ページ目に明細表と明細の下の部品が入らない（埋めない書式＝行数が決まっているとき）
  if (t.fill === false) {
    const tableBottom = t.y + tableHeight(t) + 3 + dockHeight2(layout);
    if (tableBottom > pg.h - pg.margin[2]) out.push({ kind: "space", text: `明細表と明細の下の部品が、紙の下の余白（${pg.margin[2]}mm）に入りません。行数を減らすか、行間を詰めてください`, target: "table", name: "明細表" });
  }
  // はみ出し（見本か本物の中身で）
  const overflowSeen = new Set<string>();
  for (const o of g.overflow) {
    const key = `${o.target}|${o.text}`;
    if (overflowSeen.has(key)) continue;
    overflowSeen.add(key);
    if (o.target === "table") out.push({ kind: "overflow", text: `明細表の「${o.text}」の文字が列に入りきりません`, target: "table", name: `明細の列「${o.text}」` });
    else {
      const name = nameOf(o.target);
      out.push({ kind: "overflow", text: `「${name}」の文字が枠に入りきりません（${o.text}）`, target: o.target, name });
    }
  }
  for (const p of missingRequired(layout, o)) {
    // 印字を切っている（on=false）部品なら、その部品を囲む。置いていない部品は囲めない
    const el = layout.els.find((x) => partOf(x) === p);
    out.push({ kind: "required", text: `法令上、消せない部品「${PART_NAME[p] ?? p}」が無いか、印字が切られています`, target: el?.id ?? (p === "lineItems" ? "table" : null), name: PART_NAME[p] ?? p });
  }
  return out;
}

/** 警告の文だけ（テスト印刷・前からの呼び出し） */
export function layoutWarnings(layout: PaperLayout, data?: PaperData, o: { construction?: boolean } = {}): string[] {
  return layoutWarningItems(layout, data, o).map((w) => w.text);
}

const PART_NAME: Record<string, string> = {
  title: "題字", documentNo: "番号", issueDate: "日付", recipientName: "宛名", senderName: "自社・会社名", registrationNo: "自社・登録番号", lineItems: "明細表", taxSummary: "税率ごとの内訳", totals: "合計",
  // {order}（#233 2d）
  deliveryDate: "受領期日・工期", deliveryPlace: "納品場所・工事場所", paymentDue: "支払期日", paymentTerms: "支払方法", projectTitle: "工事名・件名", orderClause: "約款・基本契約書の1行",
};
